import React from 'react';
import { BalancesWidget } from './BalancesWidget';
import { TableRow } from './TableWidget';
import { InfoIcon } from './InfoIcon';
import { useAuth } from '../../contexts/AuthContext';
import { fetchUserAccount } from '../../api/userAccount';
import { fetchTokenPrice } from '../../api/userPositions';
import { TOKENS } from '@sepori/shared/registry';

interface YieldHistoryWidgetProps {
  className?: string;
}

export const YieldHistoryWidget: React.FC<YieldHistoryWidgetProps> = ({ className }) => {
  const { user } = useAuth();
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [rows, setRows] = React.useState<TableRow[]>([]);
  const [refreshing, setRefreshing] = React.useState(false);
  const [lastUpdated, setLastUpdated] = React.useState<Date | null>(null);

  const loadData = async (bypassCache = false) => {
    try {
      setLoading(true);
      setError(null);

      if (!user?.id) {
        setRows([]);
        setLoading(false);
        return;
      }

      // Fetch user account data to get total_claimed_yield_aero
      const accountResponse = await fetchUserAccount(user.id, false);
      const totalClaimedYieldAero = accountResponse.account.total_claimed_yield_aero || 0;

      // Fetch AERO token price
      const aeroToken = TOKENS.AERO;
      const priceData = await fetchTokenPrice(aeroToken.address, { bypassCache });
      const aeroPrice = priceData.priceUsd;

      // Calculate USD value
      const value = totalClaimedYieldAero * aeroPrice;

      // Build table rows
      const newRows: TableRow[] = [
        {
          token: 'AERO',
          amount: Number(totalClaimedYieldAero).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 6
          }),
          value: `$${value.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })}`
        }
      ];

      setRows(newRows);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Failed to load yield history:', err);
      setError(err instanceof Error ? err.message : 'Failed to load yield history');
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setLoading(true);
    setRefreshing(true);
    try {
      await loadData(true); // bypassCache=true for manual refresh
    } catch (err) {
      console.error('Failed to refresh yield history:', err);
    } finally {
      setRefreshing(false);
    }
  };

  React.useEffect(() => {
    if (!user?.id) return;
    loadData();
  }, [user?.id]);

  const tooltipText = "This is the total AERO yield claimed over the lifetime of your account, shown at current price. The total does not include any \"Claimable\" yield that is waiting to be claimed from a liquidity pool.";

  return (
    <BalancesWidget
      title="Total Yield Claimed"
      titleIcon={
        <InfoIcon
          onClick={() => {}}
          title={tooltipText}
        />
      }
      rows={rows}
      loading={loading}
      error={error}
      refreshing={refreshing}
      onRefresh={loading ? () => {} : refreshData}
      lastUpdated={lastUpdated ?? undefined}
      className={className}
    />
  );
};

